import { z } from 'zod';
import { locationIdSchema } from './schemas/locations.ts';
import { isIanaTimezone } from './timezone.ts';

export const isDigestTimezone = isIanaTimezone;

export const MAX_DIGEST_LOCATIONS = 100;

export const tomorrowDigestSchema = z
  .object({
    enabled: z.boolean(),
    sendTime: z.string().regex(/^(1[6-9]|2[01]):[0-5][0-9]$/),
    timezone: z.string().max(100).refine(isDigestTimezone, 'Choose an IANA timezone'),
    locationIds: z
      .array(locationIdSchema)
      .max(MAX_DIGEST_LOCATIONS)
      .refine((ids) => new Set(ids).size === ids.length),
    sendEmpty: z.boolean(),
  })
  .strict();

// Disabling is independent of unsaved/invalid scheduling fields. Full valid
// preferences remain accepted for older clients; arbitrary keys stay forbidden.
export const tomorrowDigestUpdateSchema = z.union([
  z.object({ enabled: z.literal(false) }).strict(),
  tomorrowDigestSchema,
]);
export type TomorrowDigestUpdate = z.infer<typeof tomorrowDigestUpdateSchema>;

export type TomorrowDigestPreference = z.infer<typeof tomorrowDigestSchema>;
export interface TomorrowDigestSettings extends TomorrowDigestPreference {
  available: boolean;
  multiLocation: boolean;
  maxLocations: number;
  /** The saved selection must be changed and saved before delivery can resume. */
  selectionNeedsReview: boolean;
  companyTimezone: string;
  locations: { id: string; name: string; timezone: string }[];
}

/** Wall-clock calendar arithmetic: a DST transition never becomes a rolling 24h query. */
export function digestPlanningDate(now: Date, timezone: string, sendTime: string): string | null {
  if (!isDigestTimezone(timezone) || !/^(1[6-9]|2[01]):[0-5][0-9]$/.test(sendTime)) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  const minute = Number(part('hour')) * 60 + Number(part('minute'));
  const due = Number(sendTime.slice(0, 2)) * 60 + Number(sendTime.slice(3));
  if (minute < due || minute >= due + 120) return null;
  const date = new Date(`${part('year')}-${part('month')}-${part('day')}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}
